/**
 * Gabarit commun à /terms, /privacy et /legal — texte long
 * piloté entièrement par l'i18n (title / updated / intro / sections), une
 * <section> par thème, avec paragraphes et/ou liste à puces. Adapté de
 * memoneo (frontend/src/components/LegalPage.tsx) et rhabillé « Papier de
 * nuit ».
 *
 * Ces pages sont publiques : elles vivent hors de `RequireAuth` et hors de
 * la barre d'onglets (dont les destinations, elles, demandent un compte).
 * D'où le bouton retour en tête plutôt qu'une navigation.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Container, PageLayout, Headline, IconButton } from "./ui";
import { LegalFooter } from "./LegalFooter";

type LegalBlock = { p: string } | { list: string[] };

interface LegalSection {
  heading: string;
  blocks: LegalBlock[];
}

/** Les chaînes légales portent un balisage minimal (gras, liens). Il vient
 *  du fichier de traduction du dépôt, jamais d'une saisie utilisateur — on le
 *  passe quand même par DOMPurify, comme `Cover.tsx` le fait pour les SVG :
 *  c'est le seul `dangerouslySetInnerHTML` de l'app, autant qu'il soit propre
 *  par construction plutôt que par confiance. */
function clean(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: ["strong", "em", "a", "code", "br"], ALLOWED_ATTR: ["href", "target", "rel"] });
}

function P({ html }: { html: string }) {
  const safe = useMemo(() => clean(html), [html]);
  return (
    <p
      className="text-sm text-violet-soft font-light leading-relaxed mb-3 [&_a]:text-sun [&_a]:underline [&_a]:underline-offset-2 [&_strong]:text-paper [&_strong]:font-semibold"
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

function List({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-5 mb-3 space-y-1.5">
      {items.map((html, i) => (
        <li
          key={i}
          className="text-sm text-violet-soft font-light leading-relaxed marker:text-line [&_a]:text-sun [&_a]:underline [&_a]:underline-offset-2 [&_strong]:text-paper [&_strong]:font-semibold"
          dangerouslySetInnerHTML={{ __html: clean(html) }}
        />
      ))}
    </ul>
  );
}

export function LegalPage({ i18nKey }: { i18nKey: "privacy" | "terms" | "legal" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sections = t(`${i18nKey}.sections`, { returnObjects: true }) as unknown as LegalSection[];

  return (
    <PageLayout className="!pb-0">
      <Container width="reading" className="pt-6 pb-12 flex flex-col gap-5">
        <IconButton onClick={() => navigate(-1)} aria-label={t("common.back")} className="self-start">
          <ArrowLeft size={18} />
        </IconButton>

        <header>
          <Headline>{t(`${i18nKey}.title`)}</Headline>
          <p className="eyebrow mt-2">{t(`${i18nKey}.updated`)}</p>
        </header>

        <div>
          <P html={t(`${i18nKey}.intro`)} />

          {sections.map((section, i) => (
            <section key={i} className="mt-7">
              <h2 className="font-heading font-bold text-lg text-paper mb-2">{section.heading}</h2>
              {section.blocks.map((block, j) =>
                "list" in block ? <List key={j} items={block.list} /> : <P key={j} html={block.p} />,
              )}
            </section>
          ))}
        </div>

        <LegalFooter className="mt-4" />
      </Container>
    </PageLayout>
  );
}
